import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import axiosInstance from "../../../common/axiosInstance";
import {
  fetchHygieneScoreTrends,
  HygieneScoreTrend,
  ReportFilters,
} from "../WashroomReports/services/washroomReportsService";

interface DashboardData {
  auditsToday: number;
  compliance: number;
  pendingIssues: number;
  totalIncidents: number;
  openIncidents: number;
  pendingIncidents: number;
  resolvedIncidents: number;
  rejectedIncidents: number;
  inReviewIncidents: number;
}

const WashroomDashboard = () => {
  const [selectedPremises, setSelectedPremises] = useState("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [hygieneTrends, setHygieneTrends] = useState<HygieneScoreTrend[]>([]);
  const [trendsLoading, setTrendsLoading] = useState(false);

  const [dashboardData, setDashboardData] = useState<DashboardData>({
    auditsToday: 0,
    compliance: 0,
    pendingIssues: 0,
    totalIncidents: 0,
    openIncidents: 0,
    pendingIncidents: 0,
    resolvedIncidents: 0,
    rejectedIncidents: 0,
    inReviewIncidents: 0,
  });

  // Fetch washroom stats and incident data
  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        // Fetch washroom stats
        const washroomParams = new URLSearchParams();
        if (selectedPremises) {
          washroomParams.append("premiseId", selectedPremises);
        }
        if (selectedDate) {
          washroomParams.append("date", selectedDate);
        }

        const washroomResponse = await axiosInstance.get(
          `/api/getWashroomStats?${washroomParams.toString()}`
        );

        // Fetch incident data for Washroom Management department
        const incidentResponse = await axiosInstance.get(
          `/api/washroomincidents/department/Washroom Management`
        );

        if (!cancelled) {
          const incidents = incidentResponse.data.data || [];

          // Filter by premises if selected
          let filteredIncidents = incidents;
          if (selectedPremises) {
            filteredIncidents = incidents.filter(
              (incident: any) => incident.hospitalName === selectedPremises,
            );
          }

          // Count incidents by status
          const totalIncidents = filteredIncidents.length;
          const openIncidents = filteredIncidents.filter(
            (incident: any) => incident.status === "Open",
          ).length;
          const pendingIncidents = filteredIncidents.filter(
            (incident: any) => incident.status === "Pending",
          ).length;
          const resolvedIncidents = filteredIncidents.filter(
            (incident: any) => incident.status === "Resolved",
          ).length;
          const rejectedIncidents = filteredIncidents.filter(
            (incident: any) => incident.status === "Rejected",
          ).length;
          const inReviewIncidents = filteredIncidents.filter(
            (incident: any) => incident.status === "In Review",
          ).length;

          setDashboardData({
            auditsToday: washroomResponse?.data?.washroomsAudited || 0,
            compliance: washroomResponse?.data?.overallCompliance || 0,
            pendingIssues: pendingIncidents, // Updated to use incident data
            totalIncidents,
            openIncidents,
            pendingIncidents,
            resolvedIncidents,
            rejectedIncidents,
            inReviewIncidents,
          });
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        if (!cancelled) {
          setDashboardData((prev) => ({
            ...prev,
            auditsToday: 0,
            compliance: 0,
            pendingIssues: 0,
          }));
        }
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [selectedPremises, selectedDate]);

  // Fetch hygiene trends for the chart
  const fetchHygieneTrendsData = useCallback(async () => {
    try {
      setTrendsLoading(true);

      const filters: ReportFilters = {};

      // Only add premises filter if a premises is selected
      if (selectedPremises) {
        filters.premises = selectedPremises;
      }

      // Set date range based on dashboard requirements
      let startDate: string;
      let endDate: string;

      if (selectedDate) {
        // Use selected date as start date, today as end date
        startDate = selectedDate;
        endDate = new Date().toISOString().split("T")[0];
      } else {
        // Default to last 7 days if no date is selected
        const today = new Date();
        const weekAgo = new Date();
        weekAgo.setDate(today.getDate() - 7);

        startDate = weekAgo.toISOString().split("T")[0];
        endDate = today.toISOString().split("T")[0];
      }

      filters.start_date = startDate;
      filters.end_date = endDate;

      const trendsData = await fetchHygieneScoreTrends(filters);
      setHygieneTrends(trendsData);
    } catch (error) {
      console.error("Error fetching hygiene trends:", error);
      setHygieneTrends([]);
    } finally {
      setTrendsLoading(false);
    }
  }, [selectedPremises, selectedDate]);

  useEffect(() => {
    fetchHygieneTrendsData();
  }, [fetchHygieneTrendsData]);

  const processTrendsForChart = useCallback((trends: HygieneScoreTrend[]) => {
    if (!trends.length) return [];

    // Group by date and calculate average score per date
    const groupedByDate = trends.reduce(
      (acc, trend) => {
        const date = trend.date;
        if (!acc[date]) {
          acc[date] = {
            date,
            scores: [],
            count: 0,
          };
        }
        // Handle potential null/undefined scores
        const score = trend.hygieneScore || 0;
        acc[date].scores.push(score);
        acc[date].count += trend.auditCount || 1;
        return acc;
      },
      {} as Record<string, { date: string; scores: number[]; count: number }>,
    );

    // Calculate average score for each date and convert to chart format
    const processedTrends = Object.values(groupedByDate)
      .map((item) => {
        const avgScore =
          item.scores.length > 0
            ? item.scores.reduce((sum, score) => sum + score, 0) /
              item.scores.length
            : 0;

        const date = new Date(item.date);
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        return {
          month: monthNames[date.getMonth()],
          value: Math.round(avgScore * 100) / 100,
          date: item.date, // Keep original date for sorting
          originalDate: date,
        };
      })
      .sort((a, b) => a.originalDate.getTime() - b.originalDate.getTime());

    return processedTrends;
  }, []);

  const chartData = processTrendsForChart(hygieneTrends);

  // Create smooth path for the trend line
  const createSmoothPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return "";
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];
      const cp1x = prev.x + (curr.x - prev.x) * 0.3;
      const cp1y = prev.y;
      const cp2x = curr.x - (next ? (next.x - curr.x) * 0.3 : 0);
      const cp2y = curr.y;
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
    }
    return path;
  };

  const chartWidth = 600;
  const chartHeight = 200;
  const padding = 30;

  const dataPoints = chartData.map((d, i) => ({
    x:
      padding +
      (i * (chartWidth - 2 * padding)) / Math.max(chartData.length - 1, 1),
    y:
      chartHeight -
      padding -
      ((d.value - 50) / 50) * (chartHeight - 2 * padding),
  }));

  const smoothPath = createSmoothPath(dataPoints);

  // Calculate percentages for donut chart
  const totalActiveIncidents =
    dashboardData.openIncidents +
    dashboardData.pendingIncidents +
    dashboardData.resolvedIncidents;
  const resolvedPercentage =
    totalActiveIncidents > 0
      ? (dashboardData.resolvedIncidents / totalActiveIncidents) * 100
      : 0;
  const pendingPercentage =
    totalActiveIncidents > 0
      ? (dashboardData.pendingIncidents / totalActiveIncidents) * 100
      : 0;
  const openPercentage =
    totalActiveIncidents > 0
      ? (dashboardData.openIncidents / totalActiveIncidents) * 100
      : 0;

  // Calculate stroke-dasharray values for donut chart (circumference = 2 * π * r = 2 * π * 48 ≈ 301.59)
  const circumference = 301.59;
  const resolvedArc = (resolvedPercentage / 100) * circumference;
  const pendingArc = (pendingPercentage / 100) * circumference;
  const openArc = (openPercentage / 100) * circumference;

  return (
    <div className="px-2  py-4  max-w-8xl mx-auto mt-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1">Dashboard</h2>
          <div className="text-gray-500 text-base">
            Overview of washroom hygiene status and audit compliance
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <Dropdown
            selectedValue={selectedPremises}
            onChange={(value) => {
              setSelectedPremises(value);
            }}
            type="premises"
          />
          <input
            type="date"
            className="form-control px-3 py-2 rounded border border-gray-300"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <div className="card p-6 flex flex-col items-start">
          <div className="font-semibold text-gray-700 mb-1">
            Washrooms Audited Today
          </div>
          <div className="text-3xl font-bold">{dashboardData.auditsToday}</div>
        </div>
        <div className="card p-6 flex flex-col items-start">
          <div className="font-semibold text-gray-700 mb-1">
            Overall Compliance
          </div>
          <div className="text-3xl font-bold text-green-500">
            {dashboardData.compliance}%
          </div>
        </div>
        <div className="card p-6 flex flex-col items-start">
          <div className="font-semibold text-gray-700 mb-1">Pending Issues</div>
          <div className="text-3xl font-bold text-red-500">
            {dashboardData.pendingIssues}
          </div>
        </div>
      </div>

      {/* Main Cards: Trend and Issue Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Washroom Hygiene Trend */}
        <div className="card p-6">
          <div className="font-bold text-lg mb-4">Washroom Hygiene Trend</div>
          {trendsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">Loading trend data...</div>
            </div>
          ) : chartData.length > 0 ? (
            <div className="w-full overflow-hidden">
              <svg
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                className="w-full h-[200px]"
              >
                <defs>
                  <linearGradient
                    id="dashboardAreaGradient"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#4285f4" stopOpacity="0.3" />
                    <stop
                      offset="100%"
                      stopColor="#4285f4"
                      stopOpacity="0.05"
                    />
                  </linearGradient>
                </defs>

                {dataPoints.length > 1 && (
                  <>
                    <path
                      d={`${smoothPath} L ${
                        dataPoints[dataPoints.length - 1].x
                      } ${chartHeight - padding} L ${dataPoints[0].x} ${
                        chartHeight - padding
                      } Z`}
                      fill="url(#dashboardAreaGradient)"
                    />

                    <path
                      d={smoothPath}
                      fill="none"
                      stroke="#4285f4"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </>
                )}

                {dataPoints.map((point, i) => (
                  <circle
                    key={i}
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill="#4285f4"
                    stroke="white"
                    strokeWidth="2"
                  />
                ))}

                {chartData.map((d, i) => (
                  <text
                    key={`${d.month}-${i}`}
                    x={dataPoints[i]?.x || 0}
                    y={chartHeight - 10}
                    fontSize="12"
                    textAnchor="middle"
                    fill="#888"
                    fontWeight="500"
                  >
                    {d.month}
                  </text>
                ))}
              </svg>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No trend data available for the selected filters
            </div>
          )}
        </div>

        {/* Issue Status Donut Chart */}
        <div className="card p-6 flex flex-col items-center">
          <div className="font-bold text-lg mb-4 self-start">Issue Status</div>
          <div
            className="relative flex items-center justify-center mb-2"
            style={{ height: 140 }}
          >
            {/* Donut chart using SVG */}
            <svg width="120" height="120" viewBox="0 0 120 120">
              {/* Resolved (Green) */}
              <circle
                cx="60"
                cy="60"
                r="48"
                fill="none"
                stroke="#22c55e"
                strokeWidth="16"
                strokeDasharray={`${resolvedArc} ${
                  circumference - resolvedArc
                }`}
                strokeDashoffset="0"
                transform="rotate(-90 60 60)"
              />
              {/* Pending (Yellow) */}
              <circle
                cx="60"
                cy="60"
                r="48"
                fill="none"
                stroke="#facc15"
                strokeWidth="16"
                strokeDasharray={`${pendingArc} ${circumference - pendingArc}`}
                strokeDashoffset={`-${resolvedArc}`}
                transform="rotate(-90 60 60)"
              />
              {/* Open (Red) */}
              <circle
                cx="60"
                cy="60"
                r="48"
                fill="none"
                stroke="#ef4444"
                strokeWidth="16"
                strokeDasharray={`${openArc} ${circumference - openArc}`}
                strokeDashoffset={`-${resolvedArc + pendingArc}`}
                transform="rotate(-90 60 60)"
              />
              <text
                x="60"
                y="68"
                textAnchor="middle"
                fontSize="1.5rem"
                fontWeight="bold"
                fill="#222"
              >
                {dashboardData.totalIncidents}
              </text>
            </svg>
          </div>
          <div className="flex gap-6 mt-2">
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full bg-green-400"></span>
              <span className="text-sm">
                Resolved ({dashboardData.resolvedIncidents})
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full bg-yellow-400"></span>
              <span className="text-sm">
                Pending ({dashboardData.pendingIncidents})
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full bg-red-400"></span>
              <span className="text-sm">
                Open ({dashboardData.openIncidents})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-2">
        <div className="font-bold mb-2">Quick Actions</div>
        <div className="flex gap-3 flex-wrap">
          <Link to={`/washroom-management/washrooms/add-washroom`}>
            <button className="btn btn-primary px-5 py-2 roundedBt">
              Add Washroom
            </button>
          </Link>
          <Link to={`/washroom-management/checklists`}>
            <button className="btn btn-gray px-5 py-2 roundedBt">
              Create Checklist
            </button>
          </Link>
          <Link to={`/washroom-management/audits`}>
            <button className="btn btn-primary px-5 py-2 roundedBt">
              Start Audit
            </button>
          </Link>
          <Link to={`/washroom-management/issues-reports`}>
            <button className="btn btn-secondary px-5 py-2 roundedBt">
              View Incidents
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WashroomDashboard;
